import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  Wrench, 
  Mail, 
  LogOut,
  Plus,
  Edit,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import ProjectModal from '../components/ProjectModal';
import ClientModal from '../components/ClientModal';
import ServiceModal from '../components/ServiceModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'projects') {
        const { data } = await axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true });
        setProjects(data);
      } else if (activeTab === 'clients') {
        const { data } = await axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true });
        setClients(data);
      } else if (activeTab === 'services') {
        const { data } = await axios.get(`${BACKEND_URL}/api/services`, { withCredentials: true });
        setServices(data);
      } else if (activeTab === 'contacts') {
        const { data } = await axios.get(`${BACKEND_URL}/api/contact`, { withCredentials: true });
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/${type}/${id}`, { withCredentials: true });
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Project CRUD
  const handleSaveProject = async (projectData) => {
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/projects/${editingItem.id}`, projectData, { withCredentials: true });
        toast.success('Project updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects`, projectData, { withCredentials: true });
        toast.success('Project created successfully');
      }
      setShowProjectModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  // Client CRUD
  const handleSaveClient = async (clientData) => {
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/clients/${editingItem.id}`, clientData, { withCredentials: true });
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/clients`, clientData, { withCredentials: true });
        toast.success('Client created successfully');
      }
      setShowClientModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  // Service CRUD
  const handleSaveService = async (serviceData) => {
    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/services/${editingItem.id}`, serviceData, { withCredentials: true });
        toast.success('Service updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, serviceData, { withCredentials: true });
        toast.success('Service created successfully');
      }
      setShowServiceModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleEdit = (type, item) => {
    setEditingItem(item);
    if (type === 'projects') setShowProjectModal(true);
    else if (type === 'clients') setShowClientModal(true);
    else if (type === 'services') setShowServiceModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_profab-steel/artifacts/t4bmfg6b_qt%3Dq_95.png"
                alt="Profab Logo"
                className="h-10 w-auto"
              />
              <h1 className="ml-4 text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <nav className="flex space-x-4 p-4">
            <button
              onClick={() => setActiveTab('projects')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'projects'
                  ? 'bg-profab-blue text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Briefcase size={18} className="mr-2" />
              Projects ({projects.length})
            </button>
            <button
              onClick={() => setActiveTab('clients')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'clients'
                  ? 'bg-profab-blue text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Users size={18} className="mr-2" />
              Clients ({clients.length})
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'services'
                  ? 'bg-profab-blue text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Wrench size={18} className="mr-2" />
              Services ({services.length})
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex items-center px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === 'contacts'
                  ? 'bg-profab-blue text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Mail size={18} className="mr-2" />
              Contact Inquiries ({contacts.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-profab-blue mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <>
              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Manage Projects</h2>
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setShowProjectModal(true);
                      }}
                      className="flex items-center px-4 py-2 bg-profab-blue text-white rounded-md font-medium hover:bg-profab-blue-dark transition-colors"
                    >
                      <Plus size={18} className="mr-2" />
                      Add Project
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projects.map((project) => (
                      <div key={project.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
                        <div className="p-4">
                          <h3 className="font-semibold text-lg mb-2">{project.title}</h3>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{project.description}</p>
                          <p className="text-sm text-gray-600 mb-3">{project.location}</p>
                          <span className="inline-block px-2 py-1 bg-profab-blue/10 text-profab-blue text-xs rounded mb-3">
                            {project.category}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit('projects', project)}
                              className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                            >
                              <Edit size={16} className="mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete('projects', project.id)}
                              className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clients Tab */}
              {activeTab === 'clients' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Manage Clients</h2>
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setShowClientModal(true);
                      }}
                      className="flex items-center px-4 py-2 bg-profab-blue text-white rounded-md font-medium hover:bg-profab-blue-dark transition-colors"
                    >
                      <Plus size={18} className="mr-2" />
                      Add Client
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {clients.map((client) => (
                      <div key={client.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                        {client.logo ? (
                          <img src={client.logo} alt={client.name} className="w-full h-20 object-contain mb-3" />
                        ) : (
                          <div className="w-full h-20 flex items-center justify-center bg-gray-100 rounded mb-3">
                            <span className="text-xs text-gray-600">{client.name}</span>
                          </div>
                        )}
                        <p className="text-xs text-center font-medium text-gray-700 mb-3 line-clamp-2">{client.name}</p>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit('clients', client)}
                            className="flex-1 flex items-center justify-center px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs"
                          >
                            <Edit size={12} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete('clients', client.id)}
                            className="flex items-center justify-center px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Manage Services</h2>
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setShowServiceModal(true);
                      }}
                      className="flex items-center px-4 py-2 bg-profab-blue text-white rounded-md font-medium hover:bg-profab-blue-dark transition-colors"
                    >
                      <Plus size={18} className="mr-2" />
                      Add Service
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <div key={service.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                        <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                        <p className="text-gray-600 text-sm mb-3">{service.description}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit('services', service)}
                            className="flex-1 flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                          >
                            <Edit size={16} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete('services', service.id)}
                            className="flex items-center justify-center px-3 py-2 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Inquiries</h2>
                  <div className="space-y-4">
                    {contacts.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No inquiries yet</p>
                    ) : (
                      contacts.map((contact) => (
                        <div key={contact.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{contact.name}</h3>
                              <p className="text-sm text-gray-600">{contact.email} • {contact.phone}</p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              contact.status === 'new' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {contact.status}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-700 mb-2">{contact.subject}</p>
                          <p className="text-sm text-gray-600">{contact.message}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {new Date(contact.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveProject}
        project={editingItem}
      />

      <ClientModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveClient}
        client={editingItem}
      />

      <ServiceModal
        isOpen={showServiceModal}
        onClose={() => {
          setShowServiceModal(false);
          setEditingItem(null);
        }}
        onSave={handleSaveService}
        service={editingItem}
      />
    </div>
  );
};

export default AdminDashboard;
